/**
 * RWBR — THE COPY ON THIS DEVICE. D-EZ-99 (c).
 *
 * After every race and every settled season the shell writes the league to
 * this browser's IndexedDB, per account and per league. It is a safety net,
 * not the save: it costs no server I/O at all (Howard's reason for keeping the
 * account save manual), it survives a closed tab and a failed upload, and it is
 * what the "Couldn't reach the save server" message promises is still there.
 *
 * It stores exactly what the account save stores — the head, one record per
 * settled season, one per finished race (`leagueSlot.packLeague`) — and writes
 * only what this device does not already hold, so a backup after race 20 is one
 * race, not twenty. A small index record per league says what is held, so
 * nothing large is ever read back just to decide whether to write it.
 *
 * WHO SEES WHAT. Records are keyed by OWNER — the signed-in account's id, or
 * 'guest' when signed out — so one person's leagues are not listed to another
 * account on a shared machine.
 */

import {
  packLeague,
  readLeagueHead,
  readPackedSeason,
  readSeasonRaceRow,
  unpackLeague,
  type LeagueHead,
  type LeagueToSave,
  type LoadedLeague,
  type PackedSeason,
  type SlotFailure,
} from './leagueSlot.ts';
import type { SeasonRaceChunk } from './seasonSlot.ts';
import { SEASON_RACES } from '../domain/schedule.ts';

/** The four operations a backup needs. IndexedDB in the browser; a Map in tests. */
export interface BackupStore {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<void>;
  del(key: string): Promise<void>;
  keys(prefix: string): Promise<string[]>;
}

const DB_NAME = 'racing-slop-backup';
const DB_STORE = 'kv';

/** The browser's IndexedDB as a `BackupStore`, or null where there is none. */
export function indexedDbStore(): BackupStore | null {
  const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
  if (!idb) return null;
  let opening: Promise<IDBDatabase> | null = null;
  const db = () =>
    (opening ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = idb.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(DB_STORE)) req.result.createObjectStore(DB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    }));
  const run = <T>(mode: IDBTransactionMode, act: (s: IDBObjectStore) => IDBRequest<T>) =>
    db().then(
      (d) =>
        new Promise<T>((resolve, reject) => {
          const req = act(d.transaction(DB_STORE, mode).objectStore(DB_STORE));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        }),
    );
  return {
    get: (key) => run('readonly', (s) => s.get(key)),
    put: async (key, value) => {
      await run('readwrite', (s) => s.put(value, key));
    },
    del: async (key) => {
      await run('readwrite', (s) => s.delete(key));
    },
    keys: async (prefix) =>
      (await run('readonly', (s) => s.getAllKeys(IDBKeyRange.bound(prefix, `${prefix}￿`)))).map(String),
  };
}

/** An in-memory store — tests, and any browser with IndexedDB switched off. */
export function memoryStore(): BackupStore & { map: Map<string, unknown> } {
  const map = new Map<string, unknown>();
  return {
    map,
    get: async (key) => map.get(key),
    put: async (key, value) => void map.set(key, structuredClone(value)),
    del: async (key) => void map.delete(key),
    keys: async (prefix) => [...map.keys()].filter((k) => k.startsWith(prefix)),
  };
}

let chosen: BackupStore | null | undefined;
/** Test seam. `undefined` restores the browser default. */
export function setBackupStore(store: BackupStore | null | undefined): void {
  chosen = store;
}
function store(): BackupStore | null {
  if (chosen === undefined) chosen = indexedDbStore();
  return chosen;
}

/** Whose records these are. */
export function backupOwner(accountId: string | null | undefined): string {
  return accountId ? `acct:${accountId}` : 'guest';
}

/** One line in the "On this device" list. */
export interface BackupSummary {
  leagueId: string;
  leagueName: string;
  seasonNumber: number;
  kind: 'career' | 'season';
  racesRun: number | null;
  savedAt: string;
}

interface HeadRecord extends BackupSummary {
  head: LeagueHead;
}

interface IndexRecord {
  seasons: Record<string, number>;
  seasonKey: string | null;
  races: number[];
}

const prefixOf = (owner: string, leagueId: string) => `${owner}|${leagueId}|`;

/** "Season 3, race 12 of 36" — the line a backup is listed under. */
export function describeBackup(b: BackupSummary): string {
  return b.kind === 'season'
    ? `${b.leagueName} — season ${b.seasonNumber}, ${b.racesRun ?? 0} of ${SEASON_RACES} races run`
    : `${b.leagueName} — season ${b.seasonNumber}, finished`;
}

export type BackupResult =
  | { ok: true; savedAt: string; seasonsWritten: number; racesWritten: number }
  | { ok: false; reason: string };

/**
 * WRITE THE LEAGUE TO THIS DEVICE. Called by the shell after every race and
 * settle; quietly does nothing where there is no IndexedDB or nothing to keep.
 */
export async function backupLeague(owner: string, league: LeagueToSave, now: Date = new Date()): Promise<BackupResult> {
  const kv = store();
  if (!kv) return { ok: false, reason: 'this browser keeps no local storage' };
  const pack = packLeague(league);
  if ('ok' in pack) return { ok: false, reason: pack.message };
  const { head } = pack;
  const p = prefixOf(owner, head.leagueId);
  try {
    const index = ((await kv.get(`${p}index`)) as IndexRecord | undefined) ?? { seasons: {}, seasonKey: null, races: [] };
    let seasonsWritten = 0;
    for (const s of pack.seasons) {
      if (index.seasons[String(s.ref.seasonNumber)] === s.ref.seed) continue;
      await kv.put(`${p}season|${s.ref.seasonNumber}`, s.pack());
      index.seasons[String(s.ref.seasonNumber)] = s.ref.seed;
      seasonsWritten += 1;
    }
    let racesWritten = 0;
    if (head.kind === 'season') {
      const held = index.seasonKey === head.seasonKey ? new Set(index.races) : new Set<number>();
      for (const chunk of pack.races) {
        if (held.has(chunk.raceNumber)) continue;
        await kv.put(`${p}race|${head.seasonKey}|${chunk.raceNumber}`, chunk);
        held.add(chunk.raceNumber);
        racesWritten += 1;
      }
      index.seasonKey = head.seasonKey;
      index.races = [...held].sort((a, b) => a - b);
    } else {
      index.seasonKey = null;
      index.races = [];
    }
    const savedAt = now.toISOString();
    const record: HeadRecord = {
      leagueId: head.leagueId,
      leagueName: head.leagueName,
      seasonNumber: head.seasonNumber,
      kind: head.kind,
      racesRun: head.kind === 'season' ? head.racesRun : null,
      savedAt,
      head,
    };
    await kv.put(`${p}head`, record);
    // Tidy: races from a season this league is no longer playing, seasons it no longer names.
    const wanted = new Set(pack.seasons.map((s) => s.ref.seasonNumber));
    for (const key of await kv.keys(`${p}race|`)) {
      if (head.kind !== 'season' || !key.startsWith(`${p}race|${head.seasonKey}|`)) await kv.del(key);
    }
    for (const key of await kv.keys(`${p}season|`)) {
      const n = Number(key.slice(`${p}season|`.length));
      if (!wanted.has(n)) {
        await kv.del(key);
        delete index.seasons[String(n)];
      }
    }
    await kv.put(`${p}index`, index);
    return { ok: true, savedAt, seasonsWritten, racesWritten };
  } catch (e) {
    return { ok: false, reason: String((e as Error)?.message ?? e) };
  }
}

/** Every league this owner has on this device, newest first. */
export async function listBackups(owner: string): Promise<BackupSummary[]> {
  const kv = store();
  if (!kv) return [];
  try {
    const out: BackupSummary[] = [];
    for (const key of await kv.keys(`${owner}|`)) {
      if (!key.endsWith('|head')) continue;
      const r = (await kv.get(key)) as HeadRecord | undefined;
      if (!r) continue;
      const { head: _head, ...summary } = r;
      out.push(summary);
    }
    return out.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  } catch {
    return [];
  }
}

/** Open a league from this device. */
export async function restoreBackup(owner: string, leagueId: string): Promise<LoadedLeague | SlotFailure> {
  const kv = store();
  if (!kv) return { ok: false, code: 'unsupported-browser', message: 'This browser keeps no copy of the league.' };
  const p = prefixOf(owner, leagueId);
  try {
    const record = (await kv.get(`${p}head`)) as HeadRecord | undefined;
    if (!record) return { ok: false, code: 'no-saved-league', message: 'That league is no longer on this device.' };
    const head = readLeagueHead(record.head);
    if ('ok' in head) return head;
    const seasons: PackedSeason[] = [];
    for (const ref of head.seasons) {
      const packed = readPackedSeason(await kv.get(`${p}season|${ref.seasonNumber}`), ref, head.leagueId);
      if ('ok' in packed) return packed;
      seasons.push(packed);
    }
    const races: SeasonRaceChunk[] = [];
    if (head.kind === 'season') {
      for (let n = 1; n <= head.racesRun; n++) {
        const chunk = readSeasonRaceRow(await kv.get(`${p}race|${head.seasonKey}|${n}`), n);
        if ('ok' in chunk) return chunk;
        races.push(chunk);
      }
    }
    return unpackLeague(head, seasons, races, record.savedAt);
  } catch (e) {
    return { ok: false, code: 'corrupt', message: "The copy on this device couldn't be read.", detail: String((e as Error)?.message ?? e) };
  }
}

/** Forget one league on this device. */
export async function deleteBackup(owner: string, leagueId: string): Promise<void> {
  const kv = store();
  if (!kv) return;
  for (const key of await kv.keys(prefixOf(owner, leagueId))) await kv.del(key);
}
